/**
 * Payroll compensation — the salary component catalogue and its dual review, grade-wise CTC
 * templates, each employee's payroll profile and compensation revisions, and opening balances
 * migrated from a previous payroll system.
 *
 * DUAL REVIEW GOES THROUGH `@hrms/workflow` (invariant 6). Proposing a component or a change to one
 * (`payroll.component:propose`) raises a change request and a workflow instance; nothing about the
 * live component changes until the owner approves it. A proposed NEW component exists as
 * `pending_approval` and is never used in a calculation.
 *
 * ONE COMPENSATION HISTORY. A revision here writes an effective-dated `Compensation` row — the same
 * table `employees.listCompensation` reads — carrying the annual CTC and the template it was
 * structured with, plus the monthly Basic that the template yields. The two screens cannot drift.
 *
 * NOTHING IS VERIFIED THAT WASN'T. Bank, PAN, and UAN are reported as on file or missing. No penny
 * drop, NSDL, or EPFO check is integrated, so none is claimed.
 *
 * Money is a decimal string with its currency alongside (invariant 2).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  COMPONENT_CALCULATION_TYPES,
  COMPONENT_CHANGE_STATUSES,
  COMPONENT_TAXABILITIES,
  COMPONENT_TYPES,
  SALARY_COMPONENT_STATUSES,
} from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { DEFAULT_PAY_BASIS, payBasisAmountProblems, payBasisSchema } from '../schemas/pay-basis.js';
import { nonNegativeAmountSchema } from './payroll-settings.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const componentTypeSchema = z.enum(COMPONENT_TYPES);
export const componentCalculationTypeSchema = z.enum(COMPONENT_CALCULATION_TYPES);
export const componentTaxabilitySchema = z.enum(COMPONENT_TAXABILITIES);
export const salaryComponentStatusSchema = z.enum(SALARY_COMPONENT_STATUSES);
export const componentChangeStatusSchema = z.enum(COMPONENT_CHANGE_STATUSES);

// ---------------------------------------------------------------------------
// Salary components
// ---------------------------------------------------------------------------

/** The shape of a payroll nature key: an identifier the country pack's own registry names. The platform defines none. */
export const PAYROLL_NATURE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

const componentDefinitionFields = {
  name: z.string().trim().min(1).max(100),
  componentType: componentTypeSchema,
  calculationType: componentCalculationTypeSchema,
  /**
   * A percentage for the two percent types, a monthly amount for `fixed_monthly`, and null for
   * `balancing` and `statutory` (those derive from the rest of the structure or from statutory rules).
   */
  value: decimalStringSchema.nullable(),
  taxability: componentTaxabilitySchema,
  /** e.g. "Sec 10(13A)". */
  taxNote: z.string().trim().max(60).nullable(),
  /**
   * The tenant's classification of this component: a key of the country pack's registry of payroll natures (ADR 0012, "Company
   * components"). `null` means NOT classified, and is never read as included or excluded from any wage base. On a change
   * request, OMITTED leaves the component's current nature unchanged and `null` clears it. Optional with no default; the platform
   * defines no key and gives none a meaning.
   */
  payrollNature: z.string().regex(PAYROLL_NATURE_KEY_PATTERN).nullable().optional(),
  /**
   * Whether this earning counts in the ESI wage, for both the eligibility limit and the contribution. False for what the ESI
   * Act leaves out of "wages", e.g. a travelling allowance or leave travel concession (Section 2(22)(c)). On a change request,
   * OMITTED leaves it unchanged; a new component without it counts (true).
   */
  inEsiWage: z.boolean().optional(),
  /**
   * FBP (Flexible Benefits Plan) F1 — a DIFFERENT axis from `payrollNature` above: that classifies
   * a component for PF/ESI/gratuity WAGE-BASE inclusion; this marks it as one an employee may
   * declare an allocation against under FBP. Required (not omittable, unlike `payrollNature`) —
   * every propose/change states it explicitly, same as `componentType`/`calculationType` do.
   */
  isFlexi: z.boolean(),
  /**
   * The statutory (or, until declared otherwise, policy) annual exemption ceiling for this head.
   * Null when `isFlexi` is false, or when the limit is "whatever is declared, no statutory cap"
   * (the bill-matching heads — telephone, books, uniform). The real per-employee, per-FY
   * exemption computation (lower of declared / statutory / billed) is F4; this is the catalogue
   * entry, not the calculator.
   */
  flexiAnnualLimit: decimalStringSchema.nullable(),
  /**
   * A key into the statutory exemption-rule registry `@hrms/payroll` will carry (F6) — same shape
   * and same "the platform defines no key a meaning" convention as `payrollNature` above, reusing
   * its pattern rather than inventing a second one for the same kind of thing. Required whenever
   * `isFlexi` is true (every flexi component needs a rule key, even before F6 gives it a
   * definition); null otherwise — see the refinement below.
   */
  flexiExemptionRule: z.string().regex(PAYROLL_NATURE_KEY_PATTERN).nullable(),
};

function flexiFieldsConsistent(d: {
  isFlexi: boolean;
  flexiAnnualLimit: string | null;
  flexiExemptionRule: string | null;
}): boolean {
  if (!d.isFlexi) return d.flexiAnnualLimit === null && d.flexiExemptionRule === null;
  return d.flexiExemptionRule !== null;
}

const flexiFieldsMessage = {
  message:
    'A flexi component needs an exemption rule key; a non-flexi one must carry no flexi annual limit or rule key',
  path: ['flexiExemptionRule'],
};

function valueMatchesCalculation(d: {
  calculationType: z.infer<typeof componentCalculationTypeSchema>;
  value: string | null;
}): boolean {
  if (d.calculationType === 'balancing' || d.calculationType === 'statutory')
    return d.value === null;
  if (d.value === null) return false;
  const n = Number(d.value);
  if (n < 0) return false;
  return d.calculationType === 'fixed_monthly' || n <= 100;
}

const valueMessage = {
  message:
    'Percentages need a value from 0 to 100, fixed amounts a non-negative value, and balancing or statutory components none',
  path: ['value'],
};

export const componentDefinitionSchema = z
  .object(componentDefinitionFields)
  .refine(valueMatchesCalculation, valueMessage)
  .refine(flexiFieldsConsistent, flexiFieldsMessage);
export type ComponentDefinition = z.infer<typeof componentDefinitionSchema>;

export const salaryComponentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  ...componentDefinitionFields,
  status: salaryComponentStatusSchema,
  /** The open change request against this component, if any — one at a time. */
  pendingChangeRequestId: uuidSchema.nullable(),
  version: z.number().int().positive(),
  updatedAt: instantSchema,
});
export type SalaryComponent = z.infer<typeof salaryComponentSchema>;

export const salaryConfigSummarySchema = z.object({
  activeComponents: z.number().int().nonnegative(),
  activeEarnings: z.number().int().nonnegative(),
  activeOther: z.number().int().nonnegative(),
  pendingChanges: z.number().int().nonnegative(),
  templates: z.number().int().nonnegative(),
  /** The current PF wage ceiling of the first entity that has one on file. */
  pfWageCeiling: z
    .object({ amount: decimalStringSchema, currency: z.string(), legalEntityName: z.string() })
    .nullable(),
  /** The first three lines of the template the most employees are on — the "standard" split. */
  referenceTemplate: z
    .object({
      name: z.string(),
      splits: z.array(z.object({ label: z.string(), percentOfCtc: decimalStringSchema })),
    })
    .nullable(),
});

export const proposeComponentBodySchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'Uppercase letters, digits, underscores — e.g. EARN_LTA'),
  definition: componentDefinitionSchema,
  reason: z.string().trim().min(1).max(500),
});

export const proposeComponentChangeBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  definition: componentDefinitionSchema,
  /** Proposes deactivation (or reactivation) alongside, or instead of, a definition change. */
  isActive: z.boolean(),
  reason: z.string().trim().min(1).max(500),
});

export const componentChangeRequestSchema = z.object({
  id: uuidSchema,
  componentId: uuidSchema,
  componentCode: z.string(),
  changeType: z.enum(['create', 'update']),
  /** Null for a brand-new component. */
  current: componentDefinitionSchema.and(z.object({ isActive: z.boolean() })).nullable(),
  proposed: componentDefinitionSchema.and(z.object({ isActive: z.boolean() })),
  reason: z.string(),
  status: componentChangeStatusSchema,
  requestedByName: z.string().nullable(),
  requestedAt: instantSchema,
  decidedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decisionComment: z.string().nullable(),
  /** True when a step is awaiting THIS caller. */
  canDecide: z.boolean(),
  impact: z.object({
    templatesUsing: z.number().int().nonnegative(),
    employeesOnThoseTemplates: z.number().int().nonnegative(),
  }),
});
export type ComponentChangeRequest = z.infer<typeof componentChangeRequestSchema>;

export const decideComponentChangeBodySchema = z.object({
  comment: z.string().trim().max(500).optional(),
});

// ---------------------------------------------------------------------------
// CTC templates
// ---------------------------------------------------------------------------

export const ctcTemplateLineInputSchema = z
  .object({
    componentId: uuidSchema,
    calculationType: componentCalculationTypeSchema,
    value: decimalStringSchema.nullable(),
    /** FBP F1 — a PER-TEMPLATE opt-in, independent of the component's own `isFlexi`: a component
     *  can be globally flexi-eligible yet excluded from a specific grade's flexi pool. Defaults to
     *  false, matching the column's own default, so a caller that doesn't know about FBP yet keeps
     *  working unchanged. */
    flexiPool: z.boolean().default(false),
  })
  .refine(valueMatchesCalculation, valueMessage);

export const ctcTemplateLineSchema = z.object({
  componentId: uuidSchema,
  componentCode: z.string(),
  componentName: z.string(),
  componentType: componentTypeSchema,
  calculationType: componentCalculationTypeSchema,
  value: decimalStringSchema.nullable(),
  sortOrder: z.number().int(),
  flexiPool: z.boolean(),
});

export const ctcTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  /** Matched against an employee's current assignment grade. */
  gradeCode: z.string(),
  levelLabel: z.string().nullable(),
  minAnnualCtc: decimalStringSchema.nullable(),
  maxAnnualCtc: decimalStringSchema.nullable(),
  currency: z.string(),
  isActive: z.boolean(),
  lines: z.array(ctcTemplateLineSchema),
  /**
   * Each line's share of CTC at the template's minimum CTC (or 10,00,000 when there is none) —
   * what the grade cards draw. Computed, not stored.
   */
  illustrativeSplit: z.array(
    z.object({ componentId: uuidSchema, label: z.string(), percentOfCtc: decimalStringSchema }),
  ),
  employeeCount: z.number().int().nonnegative(),
  version: z.number().int().positive(),
});
export type CtcTemplate = z.infer<typeof ctcTemplateSchema>;

const ctcTemplateFields = {
  name: z.string().trim().min(1).max(120),
  gradeCode: z.string().trim().min(1).max(20),
  levelLabel: z.string().trim().max(40).nullable(),
  minAnnualCtc: nonNegativeAmountSchema.nullable(),
  maxAnnualCtc: nonNegativeAmountSchema.nullable(),
  currency: currencySchema,
  isActive: z.boolean(),
  lines: z.array(ctcTemplateLineInputSchema).min(1).max(25),
};

export const createCtcTemplateBodySchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, '2–30 characters: letters, digits, hyphens, underscores'),
  ...ctcTemplateFields,
});

export const updateCtcTemplateBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  ...ctcTemplateFields,
});

// ---------------------------------------------------------------------------
// Payroll profiles
// ---------------------------------------------------------------------------

export const payrollProfileRowSchema = z.object({
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  /** Null when no bank account is on file, or the caller may not see PII. */
  bank: z
    .object({
      bankName: z.string().nullable(),
      accountLast4: z.string().nullable(),
      ifsc: z.string().nullable(),
    })
    .nullable(),
  panMasked: z.string().nullable(),
  uan: z.string().nullable(),
  esicNumber: z.string().nullable(),
  /** Against the entity's current ESI wage limit; `unknown` when either side is missing. */
  esicEligibility: z.enum(['eligible', 'not_eligible', 'unknown']),
  /** What the employee's pay is specified as (ADR 0015). `ctc` for every record written before then. */
  payBasis: payBasisSchema,
  annualCtc: decimalStringSchema.nullable(),
  /** The template breakdown's monthly gross for a CTC record; the recorded monthly gross for a `gross` record. */
  monthlyGross: decimalStringSchema.nullable(),
  /** The recorded monthly take-home, for a `net` record. */
  monthlyNet: decimalStringSchema.nullable(),
  currency: z.string().nullable(),
  ctcTemplateName: z.string().nullable(),
  pendingBankChange: z.boolean(),
  kycComplete: z.boolean(),
  /** A revision dated after today, if one is scheduled. */
  scheduledRevision: z
    .object({ validFrom: dateOnlySchema, annualCtc: decimalStringSchema })
    .nullable(),
});
export type PayrollProfileRow = z.infer<typeof payrollProfileRowSchema>;

export const listPayrollProfilesQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(120).optional(),
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  kyc: z.enum(['all', 'complete', 'incomplete']).optional(),
});

export const payrollProfilesSummarySchema = z.object({
  activeProfiles: z.number().int().nonnegative(),
  /** Never summed across currencies. */
  monthlyGrossByCurrency: z.array(z.object({ currency: z.string(), amount: decimalStringSchema })),
  kycIncomplete: z.number().int().nonnegative(),
  pendingBankChanges: z.number().int().nonnegative(),
  scheduledRevisions: z.number().int().nonnegative(),
  nextRevisionDate: dateOnlySchema.nullable(),
});

export const compensationBreakdownLineSchema = z.object({
  componentCode: z.string(),
  componentName: z.string(),
  componentType: componentTypeSchema,
  calculationLabel: z.string(),
  monthly: decimalStringSchema,
  annual: decimalStringSchema,
  percentOfCtc: decimalStringSchema,
});

export const compensationBreakdownSchema = z.object({
  templateId: uuidSchema.nullable(),
  templateName: z.string().nullable(),
  annualCtc: decimalStringSchema,
  currency: z.string(),
  lines: z.array(compensationBreakdownLineSchema),
  /** Earnings only — employer contributions sit inside CTC but outside gross. */
  monthlyGross: decimalStringSchema,
  annualGross: decimalStringSchema,
  /** Set when the template's fixed lines exceed CTC and the balancing line would go negative. */
  warning: z.string().nullable(),
});
export type CompensationBreakdown = z.infer<typeof compensationBreakdownSchema>;

export const compensationRevisionSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  payBasis: payBasisSchema,
  annualCtc: decimalStringSchema.nullable(),
  monthlyGross: decimalStringSchema.nullable(),
  monthlyNet: decimalStringSchema.nullable(),
  basicSalary: decimalStringSchema.nullable(),
  currency: z.string(),
  previousAnnualCtc: decimalStringSchema.nullable(),
  templateName: z.string().nullable(),
  reason: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: instantSchema,
});
export type CompensationRevision = z.infer<typeof compensationRevisionSchema>;

export const payrollProfileDetailSchema = payrollProfileRowSchema.extend({
  pfMemberId: z.string().nullable(),
  /**
   * Pension scheme (EPS) membership: false for a member who joined EPF on or after 1 Sep 2014 above the wage ceiling
   * (the ECR then reports EPS as 0). Null = a member / not recorded. Optional so an older server's payload still parses.
   */
  epsMember: z.boolean().nullable().optional(),
  /**
   * ESI coverage: false = not covered, whatever the wage (no ESI deducted). Null = by the entity's ESI rule and the wage
   * limit. Optional so an older server's payload still parses.
   */
  esiCovered: z.boolean().nullable().optional(),
  profileVersion: z.number().int().nonnegative(),
  breakdown: compensationBreakdownSchema.nullable(),
  history: z.array(compensationRevisionSchema),
});
export type PayrollProfileDetail = z.infer<typeof payrollProfileDetailSchema>;

/**
 * `uan`, `pfMemberId` and `esicNumber`: OMITTED leaves the stored value unchanged; `null` clears it. Optional so an edit of
 * one setting (EPS membership, ESI coverage) never has to resend identifiers the caller may only see masked — the copilot
 * reads them as `[redacted]` and must not write that back.
 */
export const updatePayrollProfileBodySchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  uan: z
    .string()
    .trim()
    .regex(/^[0-9]{12}$/, 'A UAN is 12 digits')
    .nullable()
    .optional(),
  pfMemberId: z.string().trim().toUpperCase().max(30).nullable().optional(),
  esicNumber: z
    .string()
    .trim()
    .regex(/^[0-9]{10}$/, 'An ESIC insurance number is 10 digits')
    .nullable()
    .optional(),
  /** Omitted: unchanged. See `payrollProfileDetailSchema.epsMember`. */
  epsMember: z.boolean().nullable().optional(),
  /** Omitted: unchanged. See `payrollProfileDetailSchema.esiCovered`. */
  esiCovered: z.boolean().nullable().optional(),
});

const positiveAmountSchema = nonNegativeAmountSchema.refine(
  (v) => Number(v) > 0,
  'Must be greater than zero',
);

/**
 * A new effective-dated revision (ADR 0015: the pay may be specified as CTC, monthly gross or monthly net).
 *
 * EXACTLY ONE AMOUNT, THE ONE THE BASIS NAMES. `ctc` (the default, so an existing client sends nothing new) needs
 * `annualCtc` and a CTC template, as before. `gross` needs `monthlyGross` and `net` needs `monthlyNet`; neither takes
 * a template (the template calculation is a CTC one) or any other amount. The database holds the same rule as a
 * CHECK. A revision never overwrites the one before it (invariant 5): the route closes the previous row and adds
 * this one.
 */
export const reviseCompensationBodySchema = z
  .object({
    payBasis: payBasisSchema.default(DEFAULT_PAY_BASIS),
    annualCtc: positiveAmountSchema.optional(),
    monthlyGross: positiveAmountSchema.optional(),
    monthlyNet: positiveAmountSchema.optional(),
    currency: currencySchema,
    ctcTemplateId: uuidSchema.optional(),
    validFrom: dateOnlySchema,
    reason: z.string().trim().min(1).max(200),
  })
  .superRefine((value, ctx) => {
    for (const problem of payBasisAmountProblems(value.payBasis, value, {
      requireCtcAmount: true,
    })) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [problem.field],
        message: problem.message,
      });
    }
    if (value.payBasis === 'ctc' && value.ctcTemplateId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ctcTemplateId'],
        message: 'Choose a CTC template',
      });
    }
    if (value.payBasis !== 'ctc' && value.ctcTemplateId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ctcTemplateId'],
        message: 'A CTC template applies only when the pay basis is ctc',
      });
    }
  });

export const previewCompensationBodySchema = z.object({
  annualCtc: nonNegativeAmountSchema,
  currency: currencySchema,
  ctcTemplateId: uuidSchema,
});

export const listRevisionsQuerySchema = paginationQuerySchema.extend({
  scope: z.enum(['scheduled', 'recent']).default('scheduled'),
});

// ---------------------------------------------------------------------------
// Opening balances
// ---------------------------------------------------------------------------

export const openingBalanceSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  fiscalYear: z.number().int(),
  grossEarnings: decimalStringSchema,
  pfEmployee: decimalStringSchema,
  professionalTax: decimalStringSchema,
  tdsDeducted: decimalStringSchema,
  currency: z.string(),
  updatedAt: instantSchema,
});
export type OpeningBalance = z.infer<typeof openingBalanceSchema>;

export const openingBalanceRowInputSchema = z.object({
  employeeCode: z.string().trim().min(1).max(40),
  grossEarnings: nonNegativeAmountSchema,
  pfEmployee: nonNegativeAmountSchema,
  professionalTax: nonNegativeAmountSchema,
  tdsDeducted: nonNegativeAmountSchema,
  currency: currencySchema,
});

/**
 * All-or-nothing. With `dryRun` the rows are validated and nothing is written; without it, any
 * invalid row rejects the whole file (422, with every problem listed) so a half-imported ledger
 * never exists. Re-importing an employee's year replaces their figures.
 */
export const importOpeningBalancesBodySchema = z.object({
  fiscalYear: z.number().int().min(2000).max(2100),
  dryRun: z.boolean().default(false),
  rows: z.array(openingBalanceRowInputSchema).min(1).max(2000),
});

export const importOpeningBalancesResultSchema = z.object({
  dryRun: z.boolean(),
  valid: z.boolean(),
  created: z.number().int().nonnegative(),
  replaced: z.number().int().nonnegative(),
  errors: z.array(
    z.object({ row: z.number().int(), employeeCode: z.string(), message: z.string() }),
  ),
});

// ---------------------------------------------------------------------------
// Provident-fund contribution basis, per employee
// ---------------------------------------------------------------------------

/**
 * `ceiling`: PF on Basic + DA up to the platform wage ceiling (₹25,000 from 2026-09-17). `full_wage`: PF on the whole
 * Basic + DA — the joint employer–employee option of the EPF Scheme to contribute above the ceiling. The pension (EPS)
 * share stays capped at the ceiling either way; the rest goes to the PF account. `not_member`: no PF at all for this
 * employee from that date — an excluded employee under the EPF Scheme (e.g. above the wage ceiling when they joined and
 * never a member). Never an entity default.
 */
export const pfWageBasisSchema = z.enum(['ceiling', 'full_wage', 'not_member']);
export type PfWageBasis = z.infer<typeof pfWageBasisSchema>;

export const pfElectionSchema = z.object({
  id: uuidSchema,
  basis: pfWageBasisSchema,
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  reason: z.string(),
  createdByName: z.string().nullable(),
  createdAt: instantSchema,
});
export type PfElection = z.infer<typeof pfElectionSchema>;

export const pfElectionsSchema = z.object({
  /**
   * The legal entity's own default, from its PF rule in force today ("Contribute on the ceiling"): what applies on any
   * date the employee has no election for. Null when no PF rule is in force.
   */
  entityDefault: pfWageBasisSchema.nullable(),
  /** What applies today, and why: the employee's election, the entity default, or no PF at all. */
  current: z.object({
    basis: pfWageBasisSchema.nullable(),
    source: z.enum(['election', 'entity_default', 'no_pf_rule']),
  }),
  /** Newest first. Effective-dated: a new election closes the previous one the day before it starts. */
  items: z.array(pfElectionSchema),
});
export type PfElections = z.infer<typeof pfElectionsSchema>;

export const recordPfElectionBodySchema = z.object({
  basis: pfWageBasisSchema,
  validFrom: dateOnlySchema,
  /** E.g. "Joint request under the EPF Scheme, signed 1 Oct 2026". Kept with the election and in the audit log. */
  reason: z.string().trim().min(5).max(300),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const componentParams = z.object({ componentId: uuidSchema });
const changeParams = z.object({ changeRequestId: uuidSchema });
const templateParams = z.object({ templateId: uuidSchema });
const employmentParams = z.object({ employmentId: uuidSchema });

export const payrollCompensationContract = c.router(
  {
    getSalaryConfigSummary: {
      method: 'GET',
      path: '/salary-config/summary',
      responses: { 200: salaryConfigSummarySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Headline figures for the Salary Configuration screen',
      metadata: routeMeta('payroll:read'),
    },

    listComponents: {
      method: 'GET',
      path: '/components',
      query: z.object({ includeInactive: z.coerce.boolean().optional() }),
      responses: {
        200: z.object({ items: z.array(salaryComponentSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List salary components',
      metadata: routeMeta('payroll:read'),
    },

    proposeComponent: {
      method: 'POST',
      path: '/components',
      headers: idempotencyHeadersSchema,
      body: proposeComponentBodySchema,
      responses: {
        201: componentChangeRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Propose a new salary component for approval',
      metadata: routeMeta('payroll.component:propose'),
    },

    proposeComponentChange: {
      method: 'POST',
      path: '/components/:componentId/changes',
      pathParams: componentParams,
      headers: idempotencyHeadersSchema,
      body: proposeComponentChangeBodySchema,
      responses: {
        201: componentChangeRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale version, or a change is already pending for this component. */
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Propose a change to a salary component for approval',
      metadata: routeMeta('payroll.component:propose'),
    },

    listComponentChanges: {
      method: 'GET',
      path: '/component-changes',
      query: z.object({ status: componentChangeStatusSchema.optional() }),
      responses: {
        200: z.object({ items: z.array(componentChangeRequestSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List salary component change requests',
      metadata: routeMeta('payroll:read'),
    },

    approveComponentChange: {
      method: 'POST',
      path: '/component-changes/:changeRequestId/approve',
      pathParams: changeParams,
      headers: idempotencyHeadersSchema,
      body: decideComponentChangeBodySchema,
      responses: {
        200: componentChangeRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve a salary component change — applies it',
      metadata: routeMeta('payroll:read'),
    },

    rejectComponentChange: {
      method: 'POST',
      path: '/component-changes/:changeRequestId/reject',
      pathParams: changeParams,
      headers: idempotencyHeadersSchema,
      body: decideComponentChangeBodySchema,
      responses: {
        200: componentChangeRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject a salary component change',
      metadata: routeMeta('payroll:read'),
    },

    listCtcTemplates: {
      method: 'GET',
      path: '/ctc-templates',
      query: z.object({ includeInactive: z.coerce.boolean().optional() }),
      responses: {
        200: z.object({ items: z.array(ctcTemplateSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List grade-wise CTC templates',
      metadata: routeMeta('payroll:read'),
    },

    createCtcTemplate: {
      method: 'POST',
      path: '/ctc-templates',
      headers: idempotencyHeadersSchema,
      body: createCtcTemplateBodySchema,
      responses: {
        201: ctcTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a CTC template',
      metadata: routeMeta('payroll.rules:manage'),
    },

    updateCtcTemplate: {
      method: 'PUT',
      path: '/ctc-templates/:templateId',
      pathParams: templateParams,
      body: updateCtcTemplateBodySchema,
      responses: {
        200: ctcTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a CTC template',
      metadata: routeMeta('payroll.rules:manage'),
    },

    getProfilesSummary: {
      method: 'GET',
      path: '/profiles/summary',
      responses: { 200: payrollProfilesSummarySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Headline figures for the Payroll Profiles screen',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    listProfiles: {
      method: 'GET',
      path: '/profiles',
      query: listPayrollProfilesQuerySchema,
      responses: {
        200: paginatedSchema(payrollProfileRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List active employees with their payroll profile',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    getProfile: {
      method: 'GET',
      path: '/profiles/:employmentId',
      pathParams: employmentParams,
      responses: {
        200: payrollProfileDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One employee's payroll profile, CTC breakdown, and revision history",
      metadata: routeMeta('payroll:read', { salaryPin: 'subject' }),
    },

    updateProfile: {
      method: 'PATCH',
      path: '/profiles/:employmentId',
      pathParams: employmentParams,
      body: updatePayrollProfileBodySchema,
      responses: {
        200: payrollProfileDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Update an employee's UAN, PF member id, and ESIC number",
      metadata: routeMeta(['payroll.input:manage', 'payroll:read'], { salaryPin: 'others' }),
    },

    previewCompensation: {
      method: 'POST',
      path: '/compensation/preview',
      body: previewCompensationBodySchema,
      responses: {
        200: compensationBreakdownSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Compute a CTC breakdown without saving anything',
      metadata: routeMeta('payroll:read', { ai: 'read' }),
    },

    reviseCompensation: {
      method: 'POST',
      path: '/profiles/:employmentId/revisions',
      pathParams: employmentParams,
      headers: idempotencyHeadersSchema,
      body: reviseCompensationBodySchema,
      responses: {
        201: payrollProfileDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not after the current revision's start date. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Record an effective-dated compensation revision',
      metadata: routeMeta(['compensation:update', 'payroll:read'], { salaryPin: 'others' }),
    },

    listRevisions: {
      method: 'GET',
      path: '/revisions',
      query: listRevisionsQuerySchema,
      responses: {
        200: paginatedSchema(compensationRevisionSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Scheduled or recent compensation revisions',
      metadata: routeMeta(['compensation:read', 'payroll:read'], { salaryPin: 'others' }),
    },

    listOpeningBalances: {
      method: 'GET',
      path: '/opening-balances',
      query: z.object({ fiscalYear: z.coerce.number().int().min(2000).max(2100) }),
      responses: {
        200: z.object({ items: z.array(openingBalanceSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "A fiscal year's imported opening balances",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    importOpeningBalances: {
      method: 'POST',
      path: '/opening-balances/import',
      headers: idempotencyHeadersSchema,
      body: importOpeningBalancesBodySchema,
      responses: {
        200: importOpeningBalancesResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Validate or import opening balances',
      metadata: routeMeta(['import:execute', 'payroll.input:manage'], { ai: 'hidden' }),
    },

    listPfElections: {
      method: 'GET',
      path: '/profiles/:employmentId/pf-elections',
      pathParams: employmentParams,
      responses: {
        200: pfElectionsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "An employee's provident-fund contribution basis: what applies today, the entity default, and history",
      metadata: routeMeta('payroll:read'),
    },

    recordPfElection: {
      method: 'POST',
      path: '/profiles/:employmentId/pf-elections',
      pathParams: employmentParams,
      headers: idempotencyHeadersSchema,
      body: recordPfElectionBodySchema,
      responses: {
        201: pfElectionsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not after the latest election's start, or inside a period whose payroll is already locked. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Record an effective-dated change to an employee's provident-fund contribution basis",
      metadata: routeMeta(['compensation:update', 'payroll:read']),
    },
  },
  { pathPrefix: '/payroll/compensation' },
);
